import { Accordion, AccordionDetails, AccordionSummary, TextField } from "@mui/material";
import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import AutocompleteInncode from "components/common/AutocompleteInncode";
import Button from "components/common/Button";
import DialogFormation from "components/common/DialogFormation";
import Feedback from "components/common/Feedback";
import { setSavedFilterByType, updateStaticList } from "components/common/Filters/Filters.slice";
import { filterPage } from "components/common/Filters/FiltersHelper";
import { resetPageModifiedFilter } from "components/common/Filters/GlobalFilters.slice";
import Typography from "components/common/Typography";
import { updateSCList } from "components/features/SME/sme.slice";
import { useTranslate } from "hooks/useTranslate";
import { cloneDeep, isEmpty, uniqueId } from "lodash";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { resetSorting } from "redux/sorting.slice";
import { tokens } from "styles/Tokens";
import { FEEDBACK_TIMER } from "utils/CommonConstants";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import {
  setCorpSearchText,
  setCreateListError,
  setCreatedSuccess,
  setEditListSuccess,
  setIsEditListModal,
  setSelectedList,
  setSelectedListCategory
} from "../../Corp/CorpUser.Slice";
import MapUserToInnCode from "../AddUser";
import "./EditList.scss";

interface EditScSavedListProps {
  isEditListModal: boolean;
}

interface InncodeInterface {
  id: any;
  name: string;
  innCode: string;
  user: any;
  hltId?: string;
  hltIds: string[];
}

interface User {
  countryCode: string;
  abcde: string | null;
  primaryInnCode: string | null;
  lobbyUserType: string;
  hltId: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  designation: string | null;
  innCodes: string[];
  preferredEmail: string | null;
  roles: string[];
  roleCategory: string;
  roleTypes: string[];
  lastLoggedInDate: string;
  hltIds: string[];
}

export interface UsersData {
  [key: string]: User;
}

const EditScSavedList = (props: EditScSavedListProps) => {
  const translate = useTranslate();
  const { isEditListModal } = props;

  const listEditSuccess: any = useAppSelector((state) => state.CorpDashboard.listEditSuccess);
  const createListError = useAppSelector((state) => state.CorpDashboard.createListError);
  const savedFilter = useAppSelector((state) => state.filters?.savedFilter);

  const dispatch = useAppDispatch();

  const [inncodeList, setInnCodeList] = React.useState<InncodeInterface[] | []>([]);
  const [removedInncodeList, setRemovedInnCodeList] = React.useState<InncodeInterface[] | []>([]);
  const [newInncodeList, setNewInncodeList] = React.useState<InncodeInterface[] | []>([]);
  const [userList, setUserList] = React.useState<UsersData | null>(null);
  const [innCodeAssignee, setInnCodeAssignee] = useState<any>();
  const [isAddUserOpen, setAddUserOpen] = useState<boolean>(false);
  const [selectedInnCode, setSelectedInnCode] = useState<string>("");
  const [memberListIndex, setMemberListIndex] = useState<number>(0);
  const [addUserType, setAddUserType] = useState<string>("");
  const [errorIfPlanAssigned, setErrorIfPlanAssigned] = useState({
    message: "",
    isTrue: false
  });
  const [inputFieldData, setInputFieldData] = useState({
    name: "",
    desc: ""
  });

  const handleAddUser = (inncode: string, index: number, innCodeType: string) => {
    const assignee = getUserHltIdsByInnCode(inncode);
    setInnCodeAssignee(assignee);
    setSelectedInnCode(inncode);
    setAddUserOpen(true);
    setMemberListIndex(index);
    setAddUserType(innCodeType);
  };

  const deleteInnCode = (index: number, data: any) => {
    if (!data.activePlans) {
      const clonedInncodeList: InncodeInterface[] = cloneDeep(inncodeList);
      const clonedDeletedInncodeList: InncodeInterface[] = cloneDeep(removedInncodeList);
      const selectedInnCode = clonedInncodeList[index];
      clonedInncodeList.splice(index, 1);
      clonedDeletedInncodeList.unshift(selectedInnCode);
      setRemovedInnCodeList(clonedDeletedInncodeList);
      setInnCodeList(clonedInncodeList);
    } else {
      setErrorIfPlanAssigned({ message: "Inncode-User can't be deleted as there are assigned plans", isTrue: true });
    }
  };

  const resetData = () => {
    setInnCodeList([]);
    setRemovedInnCodeList([]);
    setNewInncodeList([]);
    setUserList(null);
    setInnCodeAssignee(null);
    setAddUserOpen(false);
    setSelectedInnCode("");
    setMemberListIndex(0);
    setAddUserType("");
    setErrorIfPlanAssigned({ message: "", isTrue: false });
    setInputFieldData({ name: "", desc: "" });
  };

  const getUserHltIdsByInnCode = (innCode: string) => {
    const allInncodes = [...inncodeList, ...removedInncodeList, ...newInncodeList];
    const filteredData = allInncodes.filter((obj) => obj.innCode === innCode && obj?.hltId);
    return filteredData.map((obj) => obj.hltId);
  };

  const restoreInnCode = (index: number) => {
    const clonedInncodeList: InncodeInterface[] = cloneDeep(inncodeList);
    const clonedDeletedInncodeList = cloneDeep(removedInncodeList);
    const selectedInnCode = clonedDeletedInncodeList[index];
    clonedDeletedInncodeList.splice(index, 1);
    clonedInncodeList.unshift(selectedInnCode);
    setRemovedInnCodeList(clonedDeletedInncodeList);
    setInnCodeList(clonedInncodeList);
  };

  useEffect(() => {
    if (listEditSuccess) {
      const { listData, type } = listEditSuccess;
      if (type === "static") {
        const innCodes = listData?.filterOptions?.[0]?.options || [];
        setInnCodeList(innCodes);
      } else {
        const innCodes = listData?.mappings;
        setInnCodeList(innCodes);
        setUserList(listEditSuccess.userList);
      }

      setInputFieldData({ name: listData?.name, desc: listData?.description });
    }
  }, [listEditSuccess]);

  const handleInputChange = (fieldName: string, value: string) => {
    setInputFieldData((prevState) => ({
      ...prevState,
      [fieldName]: value
    }));
  };

  const handleInncodeChange = (val: InncodeInterface) => {
    const clonedNewInncodeList: InncodeInterface[] = cloneDeep(newInncodeList);

    if (val) {
      clonedNewInncodeList.unshift(val);
      setNewInncodeList(clonedNewInncodeList);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(setEditListSuccess({ userList: null, listData: null }));
      resetData();
      dispatch(
        setCreateListError({
          isError: false,
          message: "",
          isInputError: false
        })
      );
    };
  }, []);

  const accordianSummary = (index: number, innCode: string, hltId: any, data: any) => {
    const userName = userList && userList[hltId];
    return (
      <div className="inncode-list">
        <div className="inncodes vertical-center">
          <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline} className="inncode-name">
            {innCode}
          </Typography>
        </div>
        {!userName ? (
          <div
            className={`add-user-inncode ${listEditSuccess.type === "sc" ? "" : "visibility-hidden"}`}
            onClick={() => handleAddUser(innCode, index, "inncode")}
          >
            <div className="user-icon"></div>
            <Typography variant="label" color="#007293" showBold>
              {translate("common.addTeamMember")}
            </Typography>
          </div>
        ) : (
          <div className={`user-name-wrapper ${listEditSuccess.type === "sc" ? "" : "visibility-hidden"}`}>
            <div className="user-name vertical-center">
              <Typography variant="body2" color={tokens.Text.General.Color}>
                {getUserNameWithConditionalCheck(userName?.displayName, userName?.firstName, userName?.lastName)}
              </Typography>
            </div>
          </div>
        )}
        <div className="vertical-center" onClick={() => deleteInnCode(index, data)}>
          <div className="delete-inncode"></div>
        </div>
      </div>
    );
  };

  const handleMemberList = (value: any) => {
    const clonedUserList = cloneDeep(userList);
    const hltId = value.hltId;
    const ss = {
      [hltId]: value
    };

    const clonedUserListss = { ...ss, ...clonedUserList };
    setUserList(clonedUserListss);
    if (addUserType === "inncode") {
      const clonedInnCodeList = cloneDeep(inncodeList);
      clonedInnCodeList[memberListIndex].hltIds = [value.hltId];
      setInnCodeList(clonedInnCodeList);
    } else if (addUserType === "newInncodeList") {
      const clonedNewInncodeList = cloneDeep(newInncodeList);
      clonedNewInncodeList[memberListIndex].hltId = value.hltId;
      setNewInncodeList(clonedNewInncodeList);
    }
  };

  const allInncodesList = () => {
    const newInnCodes = newInncodeList.map((innCode) => innCode.innCode);
    const allInncodes = [...inncodeList, ...removedInncodeList, ...newInnCodes];
    return allInncodes;
  };

  const deleteNewInncodes = (index: number) => {
    const clonedNewInncodeList: InncodeInterface[] = cloneDeep(newInncodeList);
    clonedNewInncodeList.splice(index, 1);
    setNewInncodeList(clonedNewInncodeList);
  };

  const formateMapping = (mapping: InncodeInterface[] | []) => {
    if (mapping.length) {
      const mapped = mapping.map((data: any) => {
        return {
          id: data.ids?.length ? data.ids[0] : null,
          innCode: data.innCode,
          hltId: data.hltIds?.[0] || data.hltId || ""
        };
      });
      return mapped;
    }
    return [];
  };

  const clearDispatchErrorSuccess = () => {
    dispatch(
      setCreateListError({
        isError: false,
        message: "",
        isInputError: false
      })
    );
    dispatch(setCreatedSuccess({ isCreated: false, message: "" }));
  };

  const handleSCSubmit = async () => {
    clearDispatchErrorSuccess();
    const clonedInncodeList = formateMapping(inncodeList);
    const clonedRemovedInncodeList = formateMapping(removedInncodeList);
    const clonedNewInncodeList = formateMapping(newInncodeList);
    const request = {
      groupId: listEditSuccess?.listData?.id,
      payload: {
        isFlagged: listEditSuccess?.listData?.isFlagged,
        description: inputFieldData.desc,
        removedMappings: clonedRemovedInncodeList,
        newMappings: clonedNewInncodeList,
        mappings: clonedInncodeList,
        name: inputFieldData.name
      }
    };
    const response = await dispatch(updateSCList(request));
    const payload = response?.payload;
    if (payload?.errors) {
      const details = payload?.errors?.[0]?.detail;
      dispatch(
        setCreateListError({
          isError: true,
          message: details,
          isInputError: details === translate("list.inncodes.already.exist")
        })
      );
    } else {
      const data = {
        data: response.payload?.consultant,
        type: "consultant"
      };
      const updatedId = listEditSuccess?.listData.id;
      const updatedList = payload.consultant?.find((element: any) => element.id === updatedId);
      dispatch(setSelectedListCategory("consultant"));
      dispatch(setSelectedList(updatedList));
      handleSubmitCommon();
      dispatch(setSavedFilterByType(data));
      dispatch(setCreatedSuccess({ isCreated: true, message: translate("list.updated.success") }));
      dispatch(setIsEditListModal(false));
    }
  };

  const handleSavedPropertySubmit = async () => {
    clearDispatchErrorSuccess();
    const newInnCodes = newInncodeList.map((data) => data.innCode);
    const mergedInnCode = [...newInnCodes, ...inncodeList];
    let selectedListData = cloneDeep(listEditSuccess.listData);
    selectedListData.name = inputFieldData.name;
    selectedListData.description = inputFieldData.desc;
    selectedListData.filters = [{ options: mergedInnCode }];

    const data = {
      static: [selectedListData]
    };
    const response = await dispatch(updateStaticList(data));
    const payload = response?.payload;
    if (payload?.data?.errors) {
      const details = payload?.data?.errors?.[0]?.detail;
      dispatch(
        setCreateListError({
          isError: true,
          message: details,
          isInputError: details === translate("list.Filter.already.exist")
        })
      );
    } else {
      const updatedId = listEditSuccess?.listData.id;
      const updatedList = payload.static?.find((element: any) => element.id === updatedId);
      const staticFilter = cloneDeep(savedFilter.static);
      const index = staticFilter.findIndex((item: any) => item.name === listEditSuccess?.listData.name);

      if (index > -1) {
        staticFilter.splice(index, 1, updatedList);
      }

      const staticPayload = {
        data: staticFilter,
        type: "static"
      };
      dispatch(setSelectedListCategory("static"));
      dispatch(setSelectedList(updatedList));
      handleSubmitCommon();
      dispatch(setCreatedSuccess({ isCreated: true, message: translate("list.updated.success") }));
      dispatch(setIsEditListModal(false));
      dispatch(setSavedFilterByType(staticPayload));
    }
  };
  const handleSubmitCommon = () => {
    dispatch(
      resetPageModifiedFilter({
        pageName: [filterPage.listDisplay, filterPage.consultantListDisplay]
      })
    );
    dispatch(
      resetSorting({
        tableName: "plansSort_myList"
      })
    );
    dispatch(
      resetSorting({
        tableName: "propertiesSort_myList"
      })
    );
    dispatch(setCorpSearchText({ view: "myList", value: "" }));
  };
  const disbaled = () => {
    return isEmpty(inputFieldData.name) || (isEmpty(inncodeList) && isEmpty(newInncodeList));
  };

  const onCloseChange = () => {
    setErrorIfPlanAssigned({ message: "", isTrue: false });
  };

  return (
    <>
      {isEditListModal && (
        <DialogFormation
          openDialog={isEditListModal}
          handleClose={() => {
            dispatch(setIsEditListModal(false));
          }}
          title={listEditSuccess.type === "sc" ? "Edit Consultant List" : translate("list.edit.list.title")}
          contentClass="edit-saved-sc-list-content"
          actionConfig={
            <div className="modal-config-wrapper">
              <Button variant="secondary" label="Cancel" onClick={() => dispatch(setIsEditListModal(false))} />
              <Button
                variant="primary"
                label="Apply Changes"
                disabled={disbaled()}
                onClick={() => (listEditSuccess.type !== "static" ? handleSCSubmit() : handleSavedPropertySubmit())}
              />
            </div>
          }
        >
          <div className="edit-saved-sc-list-component">
            {errorIfPlanAssigned.isTrue && (
              <Feedback
                form=""
                actionStatus={{ shouldShow: errorIfPlanAssigned.isTrue, variant: "error", timer: FEEDBACK_TIMER }}
                message={errorIfPlanAssigned.message}
                onCloseChange={onCloseChange}
              />
            )}
            <div className="edit-saved-sc-list-container">
              <div className="name-field">
                <Typography variant="body2" showBold color={tokens.Text.General.PrimaryBody}>
                  {translate("list.rename.list")}
                </Typography>
                <TextField
                  className="name-text"
                  error={createListError.isInputError}
                  helperText={createListError.isInputError ? createListError.message : ""}
                  value={inputFieldData.name}
                  onChange={(e: any) => {
                    handleInputChange("name", e.target.value);
                  }}
                  placeholder={translate("list.list.name.placeholder")}
                ></TextField>
              </div>
              <div className="description-field">
                <Typography variant="body2" showBold color={tokens.Text.General.PrimaryBody}>
                  {translate("list.update.description")}
                </Typography>
                <TextField
                  className="desc-text"
                  multiline={true}
                  rows={2.5}
                  value={inputFieldData.desc}
                  onChange={(e: any) => {
                    handleInputChange("desc", e.target.value);
                  }}
                  placeholder={translate("list.list.description.placeholder")}
                />
              </div>
              <div className="add-inncode-auto-complete">
                <Typography variant="body2" showBold color={tokens.Text.General.PrimaryHeadline}>
                  {translate("list.add.remove.inncodes")}
                </Typography>
                <div className="auto-complete-inncode">
                  <AutocompleteInncode
                    onSelect={handleInncodeChange}
                    isClearSearch={true}
                    placeholder="Search by Property or Inncode"
                    innCodeList={listEditSuccess.type === "static" && allInncodesList()}
                  />
                </div>
                <div className="removed-inncodes-list">
                  <Accordion className="inn-code-accordian" aria-controls="panel1-content" id="accrordian1">
                    <AccordionSummary expandIcon={<ArrowDown />} className="inn-code-accordian-list-summary">
                      <Typography variant="body2" color={tokens.Text.General.Secondary}>
                        Remove Inncodes {removedInncodeList.length > 0 ? `(${removedInncodeList.length})` : ""}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className="accordian-details">
                      <div className="inn-code-list-wrapper">
                        {removedInncodeList?.map((removedInncode: any, index: number) => {
                          const innCode = typeof removedInncode !== "string" ? removedInncode.innCode : removedInncode;
                          const hltId = typeof removedInncode !== "string" ? removedInncode.hltId : null;
                          const key = typeof removedInncode !== "string" ? removedInncode.id : removedInncode;
                          const userName = userList && userList[hltId];
                          return (
                            <div className="inncode-list" style={{ height: "40px" }} key={`remove-inncode-${key}`}>
                              <div className="inncodes vertical-center">
                                <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline}>
                                  {innCode}
                                </Typography>
                              </div>
                              {userName ? (
                                <div
                                  className={`user-name-wrapper ${
                                    listEditSuccess.type === "sc" ? "" : "visibility-hidden"
                                  }`}
                                >
                                  <div className="user-name vertical-center">
                                    <Typography variant="body2" color={tokens.Text.General.Color}>
                                      {getUserNameWithConditionalCheck(
                                        userName?.displayName,
                                        userName?.firstName,
                                        userName?.lastName
                                      )}
                                    </Typography>
                                  </div>
                                </div>
                              ) : (
                                <div></div>
                              )}

                              <div className="vertical-center" onClick={() => restoreInnCode(index)}>
                                <div className="restore-icon"></div>
                                <Typography
                                  variant="label"
                                  showBold
                                  color={tokens.Text.General.Color}
                                  className="cursor-pointer"
                                >
                                  Restore
                                </Typography>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
                <div className="removed-inncodes-list">
                  <Accordion className="inn-code-accordian" aria-controls="panel1-conten2">
                    <AccordionSummary expandIcon={<ArrowDown />} className="inn-code-accordian-list-summary">
                      <Typography variant="body2" color={tokens.Text.General.Secondary}>
                        New Inncodes {newInncodeList?.length > 0 ? `(${newInncodeList.length})` : ""}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className="accordian-details">
                      <div className="inn-code-list-wrapper">
                        {newInncodeList.length > 0 &&
                          newInncodeList.map((innCodes, index: any) => {
                            const hltId = innCodes.hltId;
                            const userName = userList && hltId && userList[hltId];

                            return (
                              <div className="inncode-list" key={`new-inncode-${innCodes.innCode}`}>
                                <div className="inncodes vertical-center">
                                  <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline}>
                                    {innCodes.innCode}
                                  </Typography>
                                </div>

                                {!userName ? (
                                  <div
                                    className={`add-user-inncode ${
                                      listEditSuccess.type === "sc" ? "" : "visibility-hidden"
                                    }`}
                                    onClick={() => handleAddUser(innCodes.innCode, index, "newInncodeList")}
                                  >
                                    <div className="user-icon"></div>
                                    <Typography variant="label" color="#007293" showBold>
                                      {translate("common.addTeamMember")}
                                    </Typography>
                                  </div>
                                ) : (
                                  <div
                                    className={`user-name-wrapper ${
                                      listEditSuccess.type === "sc" ? "" : "visibility-hidden"
                                    }`}
                                  >
                                    <div className="user-name vertical-center">
                                      <Typography variant="body2" color={tokens.Text.General.Color}>
                                        {getUserNameWithConditionalCheck(
                                          userName?.displayName,
                                          userName?.firstName,
                                          userName?.lastName
                                        )}
                                      </Typography>
                                    </div>
                                  </div>
                                )}
                                <div className="vertical-center" onClick={() => deleteNewInncodes(index)}>
                                  <div className="delete-inncode"></div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
                <div className="removed-inncodes-list">
                  <Accordion className="inn-code-accordian" key="ome">
                    <AccordionSummary expandIcon={<ArrowDown />} className="inn-code-accordian-list-summary">
                      <Typography variant="body2" color={tokens.Text.General.Secondary}>
                        Inncodes already in list {inncodeList?.length > 0 ? `(${inncodeList.length})` : ""}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className="accordian-details">
                      <div className="inn-code-list-wrapper">
                        {inncodeList &&
                          inncodeList.length > 0 &&
                          inncodeList.map((data: any, index: number) => {
                            const innCode = typeof data !== "string" ? data.innCode : data;
                            const hltId = typeof data !== "string" ? data.hltIds[0] : null;
                            return (
                              <div key={uniqueId(`inncode-${innCode}-${hltId || ""}`)}>
                                {accordianSummary(index, innCode, hltId, data)}
                              </div>
                            );
                          })}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
              </div>
            </div>
          </div>
          <MapUserToInnCode
            isAddUserOpen={isAddUserOpen}
            handleClose={() => setAddUserOpen(false)}
            selectedInnCode={selectedInnCode}
            handleMemberList={handleMemberList}
            innCodeAssignee={innCodeAssignee}
          />
        </DialogFormation>
      )}
    </>
  );
};

export default EditScSavedList;
